import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Driver, Plan } from "../types";
import { fullDriversFromResponse, type DriverListResponse } from "../driverResponses";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  plan: Plan | null;
  onClose: () => void;
  onSubscribed: () => void;
}

export default function PlanSubscribeDialog({ open, plan, onClose, onSubscribed }: Props) {
  const api = useAuthApi();
  const [driverId, setDriverId] = useState<number | "">("");
  const [code, setCode] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const loadDrivers = useCallback(async () => {
    try {
      const d = await api<DriverListResponse>(`${MGMT}/drivers`);
      setDrivers(fullDriversFromResponse(d));
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  }, [toast]);

  useEffect(() => {
    if (open) {
      setDriverId("");
      setCode("");
      setValidFrom("");
      setValidUntil("");
      loadDrivers();
    }
  }, [open, loadDrivers]);

  const handleSubscribe = async () => {
    if (!plan || driverId === "") {
      toast.show("Select a driver", "error");
      return;
    }
    if (plan.requires_code && !code.trim()) {
      toast.show("This plan requires an access code", "error");
      return;
    }
    setSaving(true);
    try {
      await api(`${MGMT}/plans/${plan.id}/subscribe`, {
        method: "POST",
        body: JSON.stringify({
          driver_id: driverId,
          code: code.trim() || undefined,
          valid_from: validFrom || undefined,
          valid_until: validUntil || undefined,
        }),
      });
      toast.show("Driver subscribed to plan — contract created");
      onSubscribed();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Subscribe to "{plan?.name}"</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth required>
            <InputLabel>Driver</InputLabel>
            <Select
              value={driverId}
              label="Driver"
              onChange={(e) => setDriverId(e.target.value as number)}
            >
              {drivers.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name} ({d.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {plan?.requires_code && (
            <TextField
              label="Access Code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              fullWidth
              inputProps={{ style: { fontFamily: "monospace", letterSpacing: 2 } }}
              helperText="Enter the unique code provided for this plan"
            />
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="Valid From"
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Valid Until"
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubscribe} disabled={saving}>
          Subscribe
        </Button>
      </DialogActions>
    </Dialog>
  );
}
